import axios from "axios";
import AuthenticationFailureHandler from "../../utils/AuthenticationFailureHandler";
import { getHeaders } from "../../utils/AuthorizationUtil";
import {
  RESERVATION_BASE_URL,
  RESERVATION_ACTION_URL,
} from "../../utils/EmpUtil";
import { is401 } from "../../errors/ErrorConstants";

export default class CustomerReservationsClient {
  static baseUrl = RESERVATION_BASE_URL;
  static actionUrl = RESERVATION_ACTION_URL;

  static getCustomerReservations(
    token,
    dealerCommonId,
    currentPage,
    lang,
    sortBy,
    marketID = null,
    selectedTab
  ) {
    let statusListString = "";
    const inProgressStatuslist = ["ordered", "created", "contracted"];
    const historyProgressList = ["cancelled", "completed"];
    let url = `${this.baseUrl}/dealer/customer-reservations`;
    dealerCommonId = dealerCommonId.split("%7C").join("|");

    if (marketID !== null) {
      url = url.concat(`?selectedMarket=${marketID}`);
    }

    if (selectedTab === 0) {
      statusListString = inProgressStatuslist.join(",");
    } else if (selectedTab === 1) {
      statusListString = historyProgressList.join(",");
    } else {
      statusListString = "";
    }

    return axios
      .post(
        url,
        {
          commonIds: dealerCommonId,
          currentPage: currentPage,
          sort: sortBy,
          lang: lang,
          status: statusListString,
        },
        getHeaders(token)
      )
      .then((response) => {
        // looking for an order with certain attributes? uncomment and modify this code
        // for (let i = 0; i < response.data.pagination.numberOfPages; i++) {
        //   axios
        //     .post(
        //       url,
        //       {
        //         commonIds: dealerCommonId,
        //         currentPage: i,
        //         sort: sortBy,
        //         lang,
        //       },
        //       getHeaders(token)
        //     )
        //     .then((page) => {
        //       for (let j = 0; j < page.data.reservations.length; j++) {
        //         this.getCustomerOrderDetails(
        //           { token },
        //           page.data.reservations[j].id,
        //           dealerCommonId,
        //           false,
        //           lang
        //         ).then((orderDetail) => {
        //           const reservation = orderDetail.customerReservation;
        //
        //           // conditions go here
        //           if (reservation.paymentType === "Lease") {
        //             console.log(`${reservation.dealerId}\n${reservation.id}`);
        //           }
        //         });
        //       }
        //     });
        // }
        return response.data;
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }
  static updateOrder(token, dealerCommonId, dealerActionInfo, dealerUserId) {
    dealerCommonId = dealerCommonId.split("|").join("%7C");
    const url = `${this.actionUrl}/dealer/${dealerCommonId}/customer-orders?dealerUserId=${dealerUserId}`;

    return axios
      .post(url, dealerActionInfo, getHeaders(token))
      .then(() => {
        return Promise.resolve();
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static updatePaymentMethod(token, paymentUpdate) {
    const url = `${this.baseUrl}/dealer/customer-reservations/payment-update`;

    return axios
      .post(url, paymentUpdate, getHeaders(token))
      .then(() => {
        return Promise.resolve();
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static updateHandOver(token, handOverData, orderId) {
    const url = `${this.baseUrl}/dealer/customer-reservations/handover-update/${orderId}`;

    return axios
      .post(url, handOverData, getHeaders(token))
      .then(() => {
        return Promise.resolve();
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static getCancelOrderReasons(
    token,
    lang,
    purchaseRequest = false,
    consentReasons = false,
    reservationReasons = false,
    vehicleHoldReasons = false
  ) {
    let url = `${this.baseUrl}/dealer/cancelOrderReasons?lang=${lang}`;

    if (purchaseRequest) {
      url = url + `&purchaseRequest=${purchaseRequest}`;
    }

    if (consentReasons) {
      url = url + `&consentReasons=true`;
    }

    if (reservationReasons) {
      url = url + `&reservationReasons=true`;
    }
    if (vehicleHoldReasons) {
      url = url + `&vehicleHoldReasons=true`;
    }

    return axios
      .get(url, getHeaders(token))
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async getCustomerOrderDetails(
    user,
    orderId,
    commonId,
    isCrc,
    lang,
    market
  ) {
    let url = `${this.baseUrl}/dealer/customer-reservations/${orderId}?lang=${lang}`;
    url = url + (isCrc ? "&market=" + market : "&dealerId=" + commonId);
    return await axios
      .get(encodeURI(url), getHeaders(user.token))
      .then((response) => response.data)
      .catch(async (error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async getCustomerFinanceDetails(user, proposalNum, market) {
    let url = `${this.baseUrl}/financialDetails/getProposalDetails?proposalNumber=${proposalNum}`;
    if (market) {
      url = url + `&market=${market}`;
    }
    return await axios
      .get(encodeURI(url), getHeaders(user.token))
      .then((response) => response.data)
      .catch(async (error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async searchCustomerReservations(
    searchQuery,
    user,
    lang,
    commonIds,
    market,
    isVistaOrderSearch = false,
    isOrbitOrderSearch = false,
    isLastNameSearch = false
  ) {
    let url = `${this.baseUrl}/dealer/customer-reservations/search?query=${searchQuery}&lang=${lang}&isVistaOrderSearch=${isVistaOrderSearch}&isOrbitOrderSearch=${isOrbitOrderSearch}&isLastNameSearch=${isLastNameSearch}`;
    if (commonIds) {
      url = url + `&commonIds=${commonIds.split("|").join("%7C")}`;
    }

    if (market) {
      url = url + `&market=${market}`;
    }
    return await axios
      .get(url, getHeaders(user.token))
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async saveValuation(valuationRequest) {
    const url = `${this.baseUrl}/dealer/customer-reservations/trade-in`;

    return axios
      .put(
        url,
        {
          expiryDate: valuationRequest.expiryDate,
          valuationAmount: valuationRequest.valuationAmount,
          outstandingFinance: valuationRequest.outstandingFinance,
          appraisalId: valuationRequest.appraisalId,
          orderId: valuationRequest.orderId,
          isOnlineTradeIn: valuationRequest.isOnlineTradeIn,
        },
        getHeaders(valuationRequest.user.token)
      )
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async saveOfflineTradein(tradeInData, user, orderId, commonId) {
    const url = `${
      this.baseUrl
    }/dealer/customer-reservations/offline-trade-in/${orderId}?commonId=${commonId
      .split("|")
      .join("%7C")}`;
    return axios
      .post(url, tradeInData, getHeaders(user.token))
      .then((response) => response.data)
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async uploadFinancialDocument(proposalNumber, financeDocumentRequest) {
    const url = `${this.baseUrl}/dealer/customer-reservations/registration-update?proposalNumber=${proposalNumber}`;
    return axios
      .post(
        url,
        financeDocumentRequest,
        getHeaders(financeDocumentRequest.token)
      )
      .then(() => Promise.resolve())
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async uploadLeaseDocument(documentType, leaseDocumentRequest) {
    const url = `${this.baseUrl}/dealer/customer-reservations/upload-document?documentType=${documentType}`;
    return axios
      .post(url, leaseDocumentRequest, getHeaders(leaseDocumentRequest.token))
      .then(() => Promise.resolve())
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static async updateRegistrationNumber(
    user,
    proposalNumber,
    acceptedDateSap,
    registrationDataRequest,
    orderId,
    paymentType,
    dealerId
  ) {
    const url = `${this.baseUrl}/dealer/customer-reservations/registration-update?proposalNumber=${proposalNumber}`;
    return axios
      .put(
        url,
        {
          commonIds: user.dealerId,
          dealerId: dealerId,
          userId: user.userId,
          acceptedDate: acceptedDateSap,
          registrationNumberDTO: registrationDataRequest,
          orderId: orderId,
          dealerAction: "registrationdetailssubmit",
          paymentType: paymentType,
        },
        getHeaders(user.token)
      )
      .then(() => Promise.resolve())
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static fetchHandOverDeliveryNotes(token, uid, vin) {
    const url = `${this.baseUrl}/dealer/customer-reservations/deliveryNotes?vin=${vin}&uid=${uid}`;

    return axios
      .get(url, getHeaders(token))
      .then((result) => {
        return result;
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }

  static updateCustomerInfoToScac(user, requestBody, guid) {
    const url = `${this.baseUrl}/dealer/update/customer-info/${guid}`;
    return axios
      .post(url, requestBody, getHeaders(user.token))
      .then(() => {
        return Promise.resolve();
      })
      .catch((error) => {
        if (is401(error)) {
          AuthenticationFailureHandler.execute();
        }
        return Promise.reject(error);
      });
  }
}
